import { HugeiconsIcon } from "@hugeicons/react";
import { CheckmarkCircle02Icon, Copy01Icon, MagicWand01Icon, Shield01Icon, TestTubeIcon } from "@hugeicons/core-free-icons";
import { useMemo, useState } from 'react';
import { MotionSection } from '@/components/shared/MotionSection';
import {
  OperationalPageHeader,
  operationalCardClass,
} from '@/components/shared/OperationalUI';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const SENSITIVE_PATTERNS = [
  { label: 'RUT', pattern: /\b\d{1,2}\.?\d{3}\.?\d{3}-[\dkK]\b/ },
  { label: 'correo', pattern: /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i },
  { label: 'teléfono', pattern: /(?:\+?56)?\s?9\s?\d{4}\s?\d{4}/ },
  { label: 'posible identificador clínico', pattern: /\b(?:ficha|paciente|diagnóstico|rut)\s*[:#-]?\s*\w+/i },
];

export default function SafeSandbox() {
  const [task, setTask] = useState('');
  const [context, setContext] = useState('');
  const [constraints, setConstraints] = useState('No inventes información. Explicita incertidumbre y solicita revisión humana.');
  const [containsRealData, setContainsRealData] = useState(false);
  const [automatedDecision, setAutomatedDecision] = useState(false);
  const [clinicalImpact, setClinicalImpact] = useState(false);
  const [output, setOutput] = useState('');

  const findings = useMemo(() => {
    const content = `${task}\n${context}\n${constraints}`;
    return SENSITIVE_PATTERNS.filter((item) => item.pattern.test(content)).map((item) => item.label);
  }, [task, context, constraints]);

  const safeToRun = findings.length === 0 && !containsRealData;
  const fastLane = safeToRun && !automatedDecision && !clinicalImpact;

  const buildPrompt = () => {
    if (!task.trim()) {
      toast.error('Describe primero la tarea que quieres resolver');
      return;
    }
    if (!safeToRun) {
      toast.error('El sandbox bloqueó la ejecución por posible información real o sensible');
      return;
    }
    setOutput(
      [
        'ROL',
        'Actúa como asistente institucional de UC CHRISTUS dentro de un entorno de experimentación sin datos reales.',
        '',
        'OBJETIVO',
        task.trim(),
        '',
        'CONTEXTO SINTÉTICO',
        context.trim() || 'No se proporcionó contexto adicional.',
        '',
        'REGLAS',
        constraints.trim(),
        '',
        'FORMATO DE RESPUESTA',
        '1. Propuesta breve. 2. Supuestos. 3. Riesgos o datos faltantes. 4. Pasos de verificación humana.',
      ].join('\n'),
    );
  };

  const copy = async () => {
    await navigator.clipboard.writeText(output);
    toast.success('Prompt seguro copiado');
  };

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-8 px-6 py-10">
      <OperationalPageHeader
        title="Sandbox seguro y Prompt Studio"
        subtitle="Experimenta sin datos reales, valida riesgos antes de avanzar y genera prompts reutilizables con supervisión humana."
        actions={<Badge variant="outline">Procesamiento local</Badge>}
      />

      <MotionSection delay={0.04}>
        <Alert className="rounded-2xl border-primary/15 bg-primary/[0.03]">
          <HugeiconsIcon icon={Shield01Icon} className="size-4" aria-hidden="true" />
          <AlertTitle>Este laboratorio no llama a ningún modelo externo</AlertTitle>
          <AlertDescription>
            Construye y valida el prompt localmente. Para ejecutar con datos institucionales debes usar una herramienta
            autorizada y la ruta de aprobación correspondiente.
          </AlertDescription>
        </Alert>
      </MotionSection>

      <MotionSection delay={0.08}>
      <div className="grid gap-6 lg:grid-cols-[1.25fr_0.75fr]">
        <Card className={operationalCardClass}>
          <CardHeader>
            <CardTitle>Diseña el experimento</CardTitle>
            <CardDescription>Usa únicamente ejemplos sintéticos o públicos.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <Label htmlFor="sandbox-task">¿Qué quieres lograr?</Label>
              <Input
                id="sandbox-task"
                value={task}
                onChange={(event) => setTask(event.target.value)}
                placeholder="Ej.: resumir un procedimiento público en una lista de verificación"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="sandbox-context">Contexto sintético</Label>
              <Textarea
                id="sandbox-context"
                value={context}
                onChange={(event) => setContext(event.target.value)}
                rows={5}
                placeholder="Describe un escenario ficticio sin nombres, RUT, fichas ni datos internos."
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="sandbox-constraints">Restricciones y criterios de calidad</Label>
              <Textarea
                id="sandbox-constraints"
                value={constraints}
                onChange={(event) => setConstraints(event.target.value)}
                rows={3}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <CheckOption
                id="real-data"
                checked={containsRealData}
                onCheckedChange={setContainsRealData}
                label="Contiene datos reales"
              />
              <CheckOption
                id="automated"
                checked={automatedDecision}
                onCheckedChange={setAutomatedDecision}
                label="Toma decisiones"
              />
              <CheckOption
                id="clinical"
                checked={clinicalImpact}
                onCheckedChange={setClinicalImpact}
                label="Impacto clínico"
              />
            </div>
            <Button
              onClick={buildPrompt}
              disabled={!safeToRun}
              className="bg-[#307FE2] text-white hover:bg-[#307FE2]/90">
              <HugeiconsIcon icon={MagicWand01Icon} data-icon="inline-start" />
              Construir prompt seguro
            </Button>
          </CardContent>
        </Card>

        <div className="flex flex-col gap-5">
          <Card className={operationalCardClass}>
            <CardHeader>
              <CardTitle>Evaluación fast lane</CardTitle>
              <CardDescription>Ruta proporcional antes de registrar una iniciativa.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm">Resultado</span>
                <Badge variant={fastLane ? 'secondary' : 'outline'}>
                  {fastLane ? 'Autoservicio de bajo riesgo' : 'Requiere evaluación'}
                </Badge>
              </div>
              <div className="flex flex-col gap-2">
                <Gate ok={findings.length === 0} label="Sin identificadores detectados" />
                <Gate ok={!containsRealData} label="Solo datos sintéticos o públicos" />
                <Gate ok={!automatedDecision} label="Sin decisión automatizada" />
                <Gate ok={!clinicalImpact} label="Sin impacto clínico directo" />
              </div>
              {findings.length > 0 && (
                <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-3">
                  <p className="text-xs font-semibold text-destructive">Posibles datos detectados</p>
                  <p className="mt-1 text-xs text-muted-foreground">{findings.join(', ')}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className={operationalCardClass}>
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <CardTitle>Prompt generado</CardTitle>
                {output && (
                  <Button variant="outline" size="sm" onClick={copy}>
                    <HugeiconsIcon icon={Copy01Icon} data-icon="inline-start" />
                    Copiar
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {output ? (
                <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-xl bg-muted p-4 text-xs leading-relaxed">
                  {output}
                </pre>
              ) : (
                <div className="flex min-h-36 flex-col items-center justify-center rounded-xl border border-dashed text-center">
                  <HugeiconsIcon icon={TestTubeIcon} className="size-7 text-muted-foreground" aria-hidden="true" />
                  <p className="mt-3 text-sm font-medium">Tu prompt aparecerá aquí</p>
                  <p className="mt-1 text-xs text-muted-foreground">Primero debe superar los controles del sandbox.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      </MotionSection>
    </div>
  );
}

function CheckOption({ id, checked, onCheckedChange, label }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-border p-3">
      <Checkbox id={id} checked={checked} onCheckedChange={onCheckedChange} />
      <Label htmlFor={id} className="text-xs leading-snug">
        {label}
      </Label>
    </div>
  );
}

function Gate({ ok, label }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {ok ? (
        <HugeiconsIcon icon={CheckmarkCircle02Icon} className="size-4 text-success" aria-hidden="true" />
      ) : (
        <HugeiconsIcon icon={Shield01Icon} className="size-4 text-warning" aria-hidden="true" />
      )}
      <span>{label}</span>
    </div>
  );
}
